import { useEffect, useState, type ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'

export interface RowGroup<T> {
  key: string
  label: string
  rows: T[]
}

/** Footer shown under a progressively-revealed presentation while more rows remain. */
export function ShowMoreFooter({ shown, total, onMore }: { shown: number; total: number; onMore: () => void }) {
  if (shown >= total) {
    return null
  }
  return (
    <div className="flex items-center justify-center gap-3 pt-2 pb-1">
      <span className="text-[11.5px] text-gis-soft tabular-nums">Showing {shown} of {total}</span>
      <button
        type="button"
        onClick={onMore}
        className="rounded-md bg-gis-track px-3 py-[5px] text-[11.5px] font-semibold text-gis-ink transition-[filter] hover:brightness-95 focus-visible:ring-2 focus-visible:ring-blue-500/60"
      >
        Show more
      </button>
    </div>
  )
}

/**
 * Collapsible group sections — sticky header (label + count + chevron) over each group's body,
 * shared by every grouped presentation on the GIS boards (Issues list/gallery, Coverage sites).
 * Presentation-only: the caller pre-computes `groups` and renders each group's rows via
 * `renderGroup`. Collapse state resets whenever the groups identity changes (new grouping or a new
 * working set) so stale collapses never hide fresh rows.
 *
 * Optional `budget`: when provided, at most `budget.visibleCount` rows mount across the expanded
 * groups (walked in order); group HEADERS always show each group's FULL count, and a Show-more footer
 * reveals the next batch. Omit it to render every row (the Coverage sites view does this) — this keeps
 * the big Issues feed from mounting all up-to-200 rows at once without changing Coverage's behavior.
 */
export function GroupedSections<T>({ groups, renderGroup, budget }: {
  groups: RowGroup<T>[]
  renderGroup: (rows: T[]) => ReactNode
  budget?: { visibleCount: number; onShowMore: () => void }
}) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  useEffect(() => {
    setCollapsed(new Set())
  }, [groups])

  const toggle = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })

  // Hand each expanded group as many rows as the running budget allows (unbudgeted → Infinity → all
  // rows). `expandedTotal` (rows in expanded groups) is what the footer counts against; collapsed
  // groups are excluded from both the budget and the total.
  let remaining = budget?.visibleCount ?? Infinity
  let expandedTotal = 0
  const slices = groups.map((g) => {
    if (collapsed.has(g.key)) {
      return [] as T[]
    }
    expandedTotal += g.rows.length
    const take = Math.min(g.rows.length, Math.max(0, remaining))
    remaining -= take
    return g.rows.slice(0, take)
  })

  return (
    <div className="space-y-1">
      {groups.map((g, idx) => {
        const isCollapsed = collapsed.has(g.key)
        const shown = slices[idx] ?? []
        return (
          <section key={g.key}>
            <button
              type="button"
              onClick={() => toggle(g.key)}
              aria-expanded={!isCollapsed}
              className="sticky top-0 z-10 flex w-full items-center gap-2 rounded-[8px] border-b border-gis-line-2 bg-white px-3 py-[6px] text-left transition-colors hover:bg-[#f7f9fc]"
            >
              <ChevronDown
                className={`h-[14px] w-[14px] flex-none text-gis-soft transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                aria-hidden
              />
              <span className="min-w-0 truncate text-[12px] font-bold text-gis-ink">{g.label}</span>
              <span className="rounded-[6px] bg-gis-track px-[7px] py-[1px] text-[10.5px] font-bold tabular-nums text-gis-ink-2">
                {g.rows.length}
              </span>
            </button>
            {!isCollapsed && shown.length > 0 && <div className="pt-1 pb-2">{renderGroup(shown)}</div>}
          </section>
        )
      })}
      {budget && (
        <ShowMoreFooter
          shown={Math.min(budget.visibleCount, expandedTotal)}
          total={expandedTotal}
          onMore={budget.onShowMore}
        />
      )}
    </div>
  )
}
